import { HttpClient } from '@angular/common/http';
import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export type LabelCarrier = 'spring' | 'ups';

export interface LabelDialogData {
  orderId: number;
  /** a rendelés megjelenítendő azonosítója (fejléc) */
  reference?: string;
  regenerate?: boolean;
  replacement?: boolean;
  replacementId?: number;
}

export interface LabelDialogResult {
  carrier: LabelCarrier;
  /** a generate-label / ups/label válasza (a rendelés, esetleg replacement_label-lel) */
  response: any;
}

interface UpsLine {
  componentId?: number | null;
  sku: string;
  description: string;
  hsCode: string;
  quantity: number;
  unitValue: number;
  unitWeightKg: number;
  originCountry: string;
  saveToComponent?: boolean;
}

interface UpsDraft {
  replacement: boolean;
  replacementId?: number | null;
  recipient: Record<string, string>;
  package: { packageType?: string | null; weightKg: number; lengthCm: number; widthCm: number; heightCm: number };
  customsRequired: boolean;
  currency: string;
  dutyPayer: 'receiver' | 'shipper';
  reasonForExport: string;
  freightCharge: number;
  lines: UpsLine[];
  serviceCode: string;
}

interface UpsDraftResponse {
  configured: boolean;
  draft: UpsDraft;
  services: Array<{ code: string; name: string }>;
  warnings: string[];
  existingLabel: { provider: string | null; trackingCode: string | null } | null;
}

interface UpsRate {
  serviceCode: string;
  serviceName: string;
  currency: string;
  total: number;
  listTotal: number | null;
  negotiated: boolean;
  businessDays: number | null;
  deliveryBy: string | null;
  warnings: string[];
}

/**
 * UPS címke (közvetlen API): címzett, csomag, vámtételek (HS kód) ellenőrzése,
 * árlekérés, szolgáltatás-választás, majd a címke. A Spring GDS (alapértelmezett)
 * a rendelés megszokott címke-gombján marad.
 */
@Component({
  selector: 'app-label-dialog',
  templateUrl: './label-dialog.component.html',
  styleUrl: './label-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class LabelDialogComponent {
  carrier: LabelCarrier = 'spring';
  busy = false;
  error = '';

  upsLoading = false;
  upsInfo: UpsDraftResponse | null = null;
  draft: UpsDraft | null = null;
  rates: UpsRate[] | null = null;
  ratesStale = false;
  selectedService = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LabelDialogData,
    private dialogRef: MatDialogRef<LabelDialogComponent, LabelDialogResult>,
    private http: HttpClient,
  ) {
    // A Spring GDS a megszokott címke-gombon megy; ez a dialógus csak a UPS-é.
    this.selectCarrier('ups');
  }

  get title(): string {
    return this.data.replacement ? 'UPS címke a csere csomaghoz' : 'UPS címke létrehozása';
  }

  get selectedRate(): UpsRate | undefined {
    return this.rates?.find((rate) => rate.serviceCode === this.selectedService);
  }

  get hsMissing(): boolean {
    return !!this.draft?.customsRequired && this.draft.lines.some((line) => !/^\d{6,10}$/.test(`${line.hsCode || ''}`.replace(/\D/g, '')));
  }

  get declaredTotal(): number {
    return (this.draft?.lines || []).reduce((sum, line) => sum + (Number(line.unitValue) || 0) * (Number(line.quantity) || 0), 0);
  }

  selectCarrier(carrier: LabelCarrier) {
    this.carrier = carrier;
    this.error = '';
    if (carrier === 'ups' && !this.upsInfo && !this.upsLoading) {
      this.loadUpsDraft();
    }
  }

  loadUpsDraft() {
    this.upsLoading = true;
    const params: Record<string, string> = {};
    if (this.data.replacement) params['replacement'] = '1';
    if (this.data.replacementId) params['replacementId'] = `${this.data.replacementId}`;
    this.http.get<UpsDraftResponse>(`orders/${this.data.orderId}/ups/draft`, { params }).subscribe({
      next: (info) => {
        this.upsLoading = false;
        this.upsInfo = info;
        this.draft = info.draft;
        this.selectedService = '';
        this.rates = null;
      },
      error: (err) => {
        this.upsLoading = false;
        this.error = err?.error?.message || err?.message || 'A UPS adatok betöltése sikertelen.';
      },
    });
  }

  /** Bármely mező változása után az ár újra lekérendő. */
  markDirty() {
    if (this.rates) this.ratesStale = true;
  }

  fetchRates() {
    if (!this.draft) return;
    this.busy = true;
    this.error = '';
    this.http.post<UpsRate[]>(`orders/${this.data.orderId}/ups/rates`, { draft: this.draft }).subscribe({
      next: (rates) => {
        this.busy = false;
        this.rates = rates || [];
        this.ratesStale = false;
        const preferred = this.rates.find((rate) => rate.serviceCode === this.draft?.serviceCode);
        this.selectedService = (preferred || this.rates[0])?.serviceCode || '';
        if (!this.rates.length) this.error = 'A UPS erre a címre nem adott szolgáltatást.';
      },
      error: (err) => {
        this.busy = false;
        this.error = err?.error?.message || err?.message || 'Az árlekérés sikertelen.';
      },
    });
  }

  createUps() {
    if (!this.draft || !this.selectedService) return;
    this.busy = true;
    this.error = '';
    const draft: UpsDraft = { ...this.draft, serviceCode: this.selectedService };
    this.http.post(`orders/${this.data.orderId}/ups/label`, { draft }).subscribe({
      next: (response) => this.dialogRef.close({ carrier: 'ups', response }),
      error: (err) => {
        this.busy = false;
        this.error = err?.error?.message || err?.message || 'A UPS címke létrehozása sikertelen.';
      },
    });
  }

  cancel() {
    this.dialogRef.close();
  }

  trackLine(index: number) {
    return index;
  }
}
